import { useState } from "react";
import { Mail, LifeBuoy, ShieldAlert, Building2, Send, CheckCircle2 } from "lucide-react";
import InfoPageLayout, { InfoSection } from "@/components/InfoPageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";

const channels = [
  {
    icon: LifeBuoy,
    title: "Product support",
    desc: "Help with your account, features, or billing.",
    email: "support@guardiannest.net",
  },
  {
    icon: ShieldAlert,
    title: "Privacy & data requests",
    desc: "Access, correction, export, or deletion.",
    email: "privacy@guardiannest.net",
  },
  {
    icon: Building2,
    title: "Partnerships & press",
    desc: "Caregiving organizations, clinicians, and media.",
    email: "hello@guardiannest.net",
  },
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") || "").trim();
    const email = String(data.get("email") || "").trim();
    const message = String(data.get("message") || "").trim();

    try {
      // Store in the anon-accessible contact_submissions table (shared with Lovable)
      const { error: err } = await supabase.from("contact_submissions").insert({
        name,
        email,
        message,
        source: "web-contact-page",
      });
      if (err) throw err;
      setSent(true);
    } catch (err) {
      // Fall back to mailto if Supabase insert fails
      const subject = encodeURIComponent(`GuardianNest inquiry from ${name || "caregiver"}`);
      const body = encodeURIComponent(`${message}\n\n— ${name}\n${email}`);
      window.location.href = `mailto:support@guardiannest.net?subject=${subject}&body=${body}`;
      setError(err?.message || "Couldn't submit — opened your email client instead.");
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <InfoPageLayout
      eyebrow="Contact"
      title="We're here for caregivers."
      intro="Real people, not chatbots. Reach the right team below or send us a note and we'll reply within one business day."
    >
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {channels.map((c) => (
            <a
              key={c.title}
              href={`mailto:${c.email}`}
              data-testid={`contact-channel-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40"
            >
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <c.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-base font-bold text-foreground">{c.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{c.desc}</p>
              <p className="mt-3 flex items-center gap-2 font-heading text-sm font-semibold text-primary">
                <Mail className="h-4 w-4" />
                {c.email}
              </p>
            </a>
          ))}
        </div>
      </section>

      <InfoSection title="Send us a message">
        {sent ? (
          <div
            className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-foreground"
            data-testid="contact-success"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="font-heading font-bold">Thanks — we've got your message.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll get back to you at the email you provided within one business day. If it's
                urgent, email{" "}
                <a
                  href="mailto:support@guardiannest.net"
                  className="font-semibold text-primary hover:underline"
                >
                  support@guardiannest.net
                </a>{" "}
                directly.
              </p>
              {error && <p className="mt-2 text-xs text-muted-foreground">({error})</p>}
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-sm"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  name="name"
                  required
                  className="mt-1.5"
                  placeholder="Alex Rivera"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="mt-1.5"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="message">How can we help?</Label>
              <Textarea
                id="message"
                name="message"
                required
                rows={5}
                className="mt-1.5"
                placeholder="Share as much detail as you like — we'll take it from there."
                data-testid="contact-message"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full sm:w-auto"
              disabled={submitting}
              data-testid="contact-submit"
            >
              <Send className="mr-2 h-4 w-4" />
              {submitting ? "Sending…" : "Send message"}
            </Button>
            <p className="text-xs text-muted-foreground">
              We'll only use your email to reply to this message.
            </p>
          </form>
        )}
      </InfoSection>

      <InfoSection title="Emergencies">
        <p>
          GuardianNest is not a substitute for emergency services. If someone is in immediate
          danger, call your local emergency number (911 in the US) right away.
        </p>
      </InfoSection>
    </InfoPageLayout>
  );
}
